import React, { useEffect, useState } from "react";
import {
  Button,
  InputLabel,
  Grid,
  Paper,
  FormControlLabel,
  Switch,
  Autocomplete,
  TextField,
  CircularProgress,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { notify } from "../../Common";
import { btnCss } from "../../../Styles";
import { apiFetch } from "../../../API/CommonApi";
import {
  getOrderSettings,
  saveOrderSettings,
} from "../../../API/OrderSettingsLayer";

const DEFAULT_SETTINGS = {
  show_products_in_form: true,
  allow_delete_order: true,
  add_prefix_to_order_id: true,
  allow_order_id_edit: true,
  list_fields: [],
};

function OrderSettings() {
  const { handleSubmit, control, reset } = useForm({
    defaultValues: DEFAULT_SETTINGS,
  });
  const [customFields, setCustomFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      getOrderSettings(),
      apiFetch("GET", "service/order-custom-field/?per_page=1000", null, true).then(
        (res) => (res.ok ? res.json() : { results: [] })
      ),
    ])
      .then(([settings, fieldsData]) => {
        setCustomFields(fieldsData.results || []);
        reset({
          show_products_in_form:
            settings?.show_products_in_form ?? true,
          allow_delete_order: settings?.allow_delete_order ?? true,
          add_prefix_to_order_id:
            settings?.add_prefix_to_order_id ?? true,
          allow_order_id_edit: settings?.allow_order_id_edit ?? true,
          list_fields: settings?.list_fields || [],
        });
      })
      .catch((err) => {
        console.error(err);
        notify("error", "Failed to load order settings");
      })
      .finally(() => setLoading(false));
  }, [reset]);

  const onSubmit = (data) => {
    setSaving(true);
    const body = {
      show_products_in_form: data.show_products_in_form,
      allow_delete_order: data.allow_delete_order,
      add_prefix_to_order_id: data.add_prefix_to_order_id,
      allow_order_id_edit: data.allow_order_id_edit,
      list_fields: (data.list_fields || []).map((field) => field.id),
    };
    saveOrderSettings(body)
      .then((res) => {
        reset({
          show_products_in_form: res.show_products_in_form,
          allow_delete_order: res.allow_delete_order,
          add_prefix_to_order_id: res.add_prefix_to_order_id,
          allow_order_id_edit: res.allow_order_id_edit,
          list_fields: res.list_fields || [],
        });
      })
      .finally(() => setSaving(false));
  };

  if (loading) {
    return (
      <div style={{ display: "grid", placeItems: "center", marginTop: 40 }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div style={{ marginTop: 20, display: "grid", placeItems: "center" }}>
      <Paper
        elevation={2}
        style={{
          margin: "20px 0",
          width: "100%",
          padding: 20,
          maxWidth: 520,
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <h2 style={{ textAlign: "center", marginBottom: 24 }}>
            Order Settings
          </h2>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                name="show_products_in_form"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!value}
                        onChange={(e) => onChange(e.target.checked)}
                      />
                    }
                    label="Show products fields in order form"
                  />
                )}
              />
              <p style={{ margin: "4px 0 0", color: "#666", fontSize: 13 }}>
                When enabled, products, dispatched products, and delivered
                products appear on the order form.
              </p>
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="allow_delete_order"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!value}
                        onChange={(e) => onChange(e.target.checked)}
                      />
                    }
                    label="Allow deleting orders"
                  />
                )}
              />
              <p style={{ margin: "4px 0 0", color: "#666", fontSize: 13 }}>
                When disabled, the delete icon is hidden on the orders list.
              </p>
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="add_prefix_to_order_id"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!value}
                        onChange={(e) => onChange(e.target.checked)}
                      />
                    }
                    label="Add prefix to order ID"
                  />
                )}
              />
              <p style={{ margin: "4px 0 0", color: "#666", fontSize: 13 }}>
                When enabled, auto-generated order IDs include company ID and
                date before the sequence number. When disabled, only the
                sequence number is used.
              </p>
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="allow_order_id_edit"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!value}
                        onChange={(e) => onChange(e.target.checked)}
                      />
                    }
                    label="Allow order ID edit"
                  />
                )}
              />
              <p style={{ margin: "4px 0 0", color: "#666", fontSize: 13 }}>
                When enabled, the Order ID field is shown on the create order
                form so users can enter a custom ID. When disabled, the field
                is hidden and the ID is auto-generated.
              </p>
            </Grid>

            <Grid item xs={12}>
              <InputLabel sx={{ color: "#303535", marginBottom: 1 }}>
                List table custom fields
              </InputLabel>
              <Controller
                name="list_fields"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Autocomplete
                    multiple
                    options={customFields}
                    value={value || []}
                    isOptionEqualToValue={(option, val) => option.id === val.id}
                    getOptionLabel={(option) =>
                      (option.name || "").replace(/_/g, " ")
                    }
                    onChange={(_, newValue) => onChange(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Select custom fields"
                      />
                    )}
                  />
                )}
              />
              <p style={{ margin: "8px 0 0", color: "#666", fontSize: 13 }}>
                Selected fields replace Updated at on the orders list. Order ID,
                Created at, name, and phone always remain visible.
              </p>
            </Grid>

            <Grid item xs={12} style={{ textAlign: "center" }}>
              <Button
                type="submit"
                variant="contained"
                sx={btnCss}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </div>
  );
}

export default OrderSettings;
